import { memo, useCallback, useRef } from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { Overlay } from '../../Overlay/Overlay';
import { trackButtonClickEvent } from '../../../services/tracking/pulseEvents';
import { getCurrentPageViewData } from '../../../services/tracking/helpers/pulseOrigin';
import { AssetsSchedule } from '../../AssetsSchedule/AssetsSchedule';
import { useAssetsScheduleProps } from '../../AssetsSchedule/useAssetsScheduleProps';
import { useQuery } from '../../utils/useQuery';
import { useVendor, VendorProvider } from '../../VendorPovider/VendorPovider';

import { ComponentPopupContent } from './ComponentPopupContent/ComponentPopupContent';
import { getComponentConfig } from './helpers';

import type { Component, ComponentId } from '@vgtv/api-client/lib/component';
import type { VFC } from 'react';

import * as styles from './ComponentPopup.module.scss';

export const POPUP_ID_PARAM = 'popup_id';

interface ComponentPopupProps {
  componentsConfig: Component[];
}

const ComponentPopupBase: VFC<ComponentPopupProps> = ({ componentsConfig }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const { vendor } = useVendor();

  const { popup_id: popupId } = useQuery<typeof POPUP_ID_PARAM>();
  const componentConfig = popupId
    ? getComponentConfig(popupId as ComponentId, componentsConfig)
    : undefined;

  const isOpen = Boolean(popupId && componentConfig);

  const { isLiveScheduleSupported, categoryId, tagId } =
    useAssetsScheduleProps(componentConfig);

  const onCloseClick = useCallback(() => {
    const currentPageViewData = getCurrentPageViewData();

    const button = {
      elementType: 'CloseButton',
      title: 'Close',
    };
    const target = {
      elementType: 'AssetsSlider',
      name: componentConfig!.header!,
      url: currentPageViewData.url,
    };
    const parent = {
      elementType: 'ComponentPopup',
      name: componentConfig!.header!,
    };

    trackButtonClickEvent(button, target, parent);

    history.goBack();
  }, [componentConfig, history]);

  return (
    <Overlay
      type="overlay/popup"
      isOpen={isOpen}
      onCloseButtonClick={isLiveScheduleSupported ? undefined : onCloseClick}
      contentClassName={classNames(styles.popupContent, {
        [styles.liveSchedulePopupContent]: isLiveScheduleSupported,
      })}
      scrollContainerRef={scrollContainerRef}
    >
      {componentConfig &&
        (isLiveScheduleSupported ? (
          <AssetsSchedule
            categoryId={categoryId}
            tagId={tagId}
            title={componentConfig.header!}
            scrollContainerRef={scrollContainerRef}
            onCloseButtonClick={onCloseClick}
          />
        ) : (
          <VendorProvider
            vendor={
              'provider' in componentConfig ? componentConfig.provider : vendor
            }
          >
            <ComponentPopupContent componentConfig={componentConfig} />
          </VendorProvider>
        ))}
    </Overlay>
  );
};

export const ComponentPopup = memo(ComponentPopupBase);
